import React, { useEffect, useState } from 'react';
import { getCosts } from '../../lib/api';
import { ArrowDown, ArrowUp, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { CATEGORIES, CATEGORY_LABEL, fmtEuro } from './shared';

/**
 * Compares forecast (categorie="budget") vs actual (categorie="charges")
 * for a given dossier. Renders a compact comparison table:
 *   Catégorie | Prévu | Réalisé | Écart € | Écart %
 *
 * The widget fetches its own data so it stays in sync after a parent update.
 * Pass `refreshKey` (e.g. dossier.updated_at) to force a refresh from outside.
 */
export const BudgetVsRealiseWidget = ({ dossierId, refreshKey }) => {
    const [budget, setBudget] = useState([]);
    const [charges, setCharges] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const [bRes, cRes] = await Promise.all([
                    getCosts(dossierId, 'budget'),
                    getCosts(dossierId, 'charges'),
                ]);
                if (cancelled) return;
                setBudget(bRes.data || []);
                setCharges(cRes.data || []);
            } catch {
                if (!cancelled) {
                    setBudget([]);
                    setCharges([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [dossierId, refreshKey]);

    const sumByCat = (items) => {
        const out = {};
        items.forEach((i) => {
            const k = i.type_cout || 'autre';
            out[k] = (out[k] || 0) + Number(i.montant || 0);
        });
        return out;
    };

    const bByCat = sumByCat(budget);
    const cByCat = sumByCat(charges);

    const rows = CATEGORIES.map((cat) => {
        const prev = bByCat[cat.value] || 0;
        const real = cByCat[cat.value] || 0;
        const ecart = real - prev;
        const ecartPct = prev > 0 ? (ecart / prev) * 100 : (real > 0 ? Infinity : 0);
        return { ...cat, prev, real, ecart, ecartPct };
    }).filter((r) => r.prev > 0 || r.real > 0);

    const totalPrev = Object.values(bByCat).reduce((a, b) => a + b, 0);
    const totalReal = Object.values(cByCat).reduce((a, b) => a + b, 0);
    const totalEcart = totalReal - totalPrev;
    const totalEcartPct = totalPrev > 0 ? (totalEcart / totalPrev) * 100 : (totalReal > 0 ? Infinity : 0);
    const hasData = totalPrev > 0 || totalReal > 0;

    const renderEcart = (ecart, pct) => {
        if (Math.abs(ecart) < 0.5) {
            return (
                <span className="inline-flex items-center gap-1 text-[#52525B]">
                    <Minus className="w-3.5 h-3.5" /> ±0
                </span>
            );
        }
        const over = ecart > 0; // realisé > prévu (mauvais)
        const cls = over ? 'text-red-600' : 'text-[#16A34A]';
        const Icon = over ? ArrowUp : ArrowDown;
        const pctText = Number.isFinite(pct) ? `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%` : 'n/a';
        return (
            <span className={`inline-flex items-center gap-1 font-semibold ${cls}`}>
                <Icon className="w-3.5 h-3.5" />
                {over ? '+' : ''}{fmtEuro(ecart)} € · {pctText}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm p-4 text-sm text-[#52525B]" data-testid="budget-vs-realise-widget">
                Chargement de la comparaison Budget vs Réalisé…
            </div>
        );
    }

    if (!hasData) {
        return (
            <div className="bg-[#FAFAFA] border border-dashed border-[#E4E4E7] rounded-sm p-4 text-sm text-[#52525B] italic" data-testid="budget-vs-realise-widget">
                Aucune donnée à comparer pour l&apos;instant. Ajoutez des lignes Budget prévisionnel et/ou Charges pour activer le suivi d&apos;écart.
            </div>
        );
    }

    const headerIcon = totalEcart > 0 ? TrendingUp : TrendingDown;
    const HeaderIcon = headerIcon;
    const headerCls = totalEcart > 0 ? 'text-red-600' : 'text-[#16A34A]';
    const headerLabel = totalEcart > 0
        ? 'Dépassement par rapport au budget prévisionnel'
        : (totalEcart < 0 ? 'En-deçà du budget prévisionnel' : 'Budget tenu au centime près');

    return (
        <div className="bg-white border border-[#E4E4E7] rounded-sm" data-testid="budget-vs-realise-widget">
            <div className="px-4 py-3 border-b border-[#E4E4E7] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="flex items-center gap-2">
                    <HeaderIcon className={`w-5 h-5 ${headerCls}`} />
                    <h4 className="font-semibold text-[#0A0A0A]">Écart Budget vs Réalisé</h4>
                </div>
                <p className={`text-sm ${headerCls}`}>
                    <span className="text-[#52525B] mr-1">{headerLabel} :</span>
                    <span className="font-semibold">
                        {totalEcart >= 0 ? '+' : ''}{fmtEuro(totalEcart)} € HT
                        {Number.isFinite(totalEcartPct) && ` (${totalEcartPct >= 0 ? '+' : ''}${totalEcartPct.toFixed(1)}%)`}
                    </span>
                </p>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-[#FAFAFA] text-[#52525B] text-xs uppercase">
                        <tr>
                            <th className="text-left px-3 py-2 font-medium">Catégorie</th>
                            <th className="text-right px-3 py-2 font-medium">Prévu</th>
                            <th className="text-right px-3 py-2 font-medium">Réalisé</th>
                            <th className="text-right px-3 py-2 font-medium">Écart</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E4E4E7]">
                        {rows.map((r) => (
                            <tr key={r.value} data-testid={`bvr-row-${r.value}`}>
                                <td className="px-3 py-2 text-[#0A0A0A]">{CATEGORY_LABEL[r.value] || r.label}</td>
                                <td className="px-3 py-2 text-right">{fmtEuro(r.prev)} €</td>
                                <td className="px-3 py-2 text-right">{fmtEuro(r.real)} €</td>
                                <td className="px-3 py-2 text-right">{renderEcart(r.ecart, r.ecartPct)}</td>
                            </tr>
                        ))}
                        <tr className="bg-[#FAFAFA] font-bold" data-testid="bvr-row-total">
                            <td className="px-3 py-2">Total</td>
                            <td className="px-3 py-2 text-right">{fmtEuro(totalPrev)} €</td>
                            <td className="px-3 py-2 text-right">{fmtEuro(totalReal)} €</td>
                            <td className="px-3 py-2 text-right">{renderEcart(totalEcart, totalEcartPct)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};
